import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { getOfflineDrivePositions } from 'src/apis/offlineDrive';
import Select from 'react-select';

interface DriveCompanySelectionWithIdProps {
    selectedCompany?: { label: string; value: string };
    onChange: (value: { label: string; value: string; logo: string; url: string } | null) => void;
    driveId: string;
}

function DriveCompanySelectionWithId({
    selectedCompany,
    onChange,
    driveId,
}: DriveCompanySelectionWithIdProps) {
    const [searchTerm, setSearchTerm] = useState('');

    // Load all positions for the drive to extract companies
    const { data: positionsData, isLoading } = useQuery(
        ['driveCompaniesFromPositions', driveId],
        () =>
            getOfflineDrivePositions({
                driveId,
                page: 0,
                limit: 10000, // Large limit to get all positions
            }),
        {
            staleTime: 60 * 1000,
            enabled: !!driveId,
        },
    );

    const items = useMemo(() => {
        if (!positionsData?.positions) return [];

        // Extract unique companies from positions
        const companyMap = new Map<string, { label: string; value: string; logo: string; url: string }>();

        positionsData.positions.forEach(position => {
            if (position?.company?._id && !companyMap.has(position.company._id.toString())) {
                companyMap.set(position.company._id.toString(), {
                    label: position.company.name || '',
                    value: position.company._id.toString(),
                    logo: position.company?.logo || '',
                    url: position.company?.url || '',
                });
            }
        });

        let companies = Array.from(companyMap.values());

        // Sort companies alphabetically by name
        companies.sort((a, b) => a.label.localeCompare(b.label));

        // Filter by search term if provided
        if (searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase();
            companies = companies.filter(c => c.label.toLowerCase().includes(searchLower));
        }

        return companies;
    }, [positionsData?.positions, searchTerm]);

    return (
        <div>
            <Select
                required
                options={items}
                menuPosition="fixed"
                isLoading={isLoading}
                menuPlacement={'auto'}
                value={selectedCompany}
                classNamePrefix="flowbite-select"
                inputValue={searchTerm}
                onInputChange={value => {
                    setSearchTerm(value);
                }}
                onChange={selection =>
                    onChange(selection as { label: string; value: string; logo: string; url: string } | null)
                }
                placeholder="Select Company"
                isSearchable={true}
            />
        </div>
    );
}

export default DriveCompanySelectionWithId;

